import * as THREE from 'three';
import { box, cyl, M, mat, mesh, sphere, texMat } from '../materials';
import { signTexture, type SignSpec } from '../textures';

export type CarKind = 'camaro' | 'sedan' | 'pickup' | 'cop' | 'van';

export interface CarSpec {
  kind: CarKind;
  color: string;
  hood?: string;
  door?: string;
  onBlocks?: boolean;
  rusty?: boolean;
}

export interface BuiltCar {
  group: THREE.Group;
  /** Wheel meshes (spin them while driving). */
  wheels: THREE.Mesh[];
  /** Body group: everything but wheels and blocks. Lift it for "on blocks". */
  body: THREE.Group;
  /** Red/blue light-bar materials for cop cars. */
  lightBar?: [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial];
  /** Attach point at the rear bumper (for Truck Nutz). */
  rear: THREE.Group;
  length: number;
  width: number;
}

function profile(points: Array<[number, number]>, depth: number, material: THREE.Material): THREE.Mesh {
  const shape = new THREE.Shape();
  shape.moveTo(points[0]![0], points[0]![1]);
  for (let i = 1; i < points.length; i++) shape.lineTo(points[i]![0], points[i]![1]);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelSize: 0.04,
    bevelThickness: 0.04,
    bevelSegments: 1,
  });
  geo.translate(0, 0, -depth / 2);
  const m = new THREE.Mesh(geo, material);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/** Car facing +x. Origin on the ground under the center. */
export function buildCar(spec: CarSpec): BuiltCar {
  const group = new THREE.Group();
  const body = new THREE.Group();
  group.add(body);
  const paint = mat(spec.color, { rough: spec.rusty ? 0.9 : 0.45, metal: spec.rusty ? 0 : 0.25 });
  const glass = M.darkGlass;
  const rear = new THREE.Group();
  body.add(rear);
  const wheels: THREE.Mesh[] = [];
  let length = 4.6;
  let width = 1.8;
  let wheelX = 1.45;
  let lightBar: BuiltCar['lightBar'];

  switch (spec.kind) {
    case 'camaro': {
      length = 4.7;
      body.add(
        profile(
          [
            [-2.35, 0.3],
            [-2.35, 0.78],
            [-2.15, 0.93],
            [-1.3, 0.98],
            [1.05, 0.96],
            [2.2, 0.8],
            [2.35, 0.62],
            [2.35, 0.3],
          ],
          1.72,
          paint,
        ),
      );
      body.add(
        profile(
          [
            [-1.35, 0.96],
            [-0.42, 1.3],
            [0.42, 1.3],
            [1.08, 0.95],
          ],
          1.46,
          glass,
        ),
      );
      body.add(box(0.86, 0.05, 1.5, paint, 0, 1.34, 0));
      if (spec.hood) {
        const hood = box(1.18, 0.03, 1.62, mat(spec.hood, { rough: 0.95 }), 1.62, 0.92, 0);
        hood.rotation.z = -0.13;
        body.add(hood);
      }
      if (spec.door) body.add(box(1.1, 0.48, 0.03, mat(spec.door, { rough: 0.9 }), -0.05, 0.68, 0.9));
      body.add(box(0.12, 0.05, 1.7, M.black, -2.18, 1.1, 0));
      for (const z of [-0.6, 0.6]) body.add(box(0.06, 0.14, 0.06, M.black, -2.18, 1.0, z));
      for (const z of [-0.55, 0.55])
        body.add(
          box(0.05, 0.12, 0.4, mat('#c4202a', { emissive: '#5a0000' }), -2.4, 0.66, z, { cast: false }),
        );
      for (const z of [-0.55, 0.55])
        body.add(box(0.05, 0.1, 0.36, mat('#f3e9b0'), 2.4, 0.5, z, { cast: false }));
      rear.position.set(-2.42, 0.42, 0);
      break;
    }
    case 'sedan':
    case 'cop': {
      body.add(
        profile(
          [
            [-2.3, 0.3],
            [-2.3, 0.84],
            [-2.0, 0.94],
            [-1.3, 0.96],
            [1.2, 0.96],
            [2.2, 0.86],
            [2.3, 0.64],
            [2.3, 0.3],
          ],
          1.76,
          paint,
        ),
      );
      body.add(
        profile(
          [
            [-1.3, 0.95],
            [-0.85, 1.42],
            [0.7, 1.42],
            [1.22, 0.95],
          ],
          1.56,
          glass,
        ),
      );
      body.add(box(1.5, 0.05, 1.6, paint, -0.07, 1.46, 0));
      if (spec.kind === 'cop') {
        for (const z of [-0.9, 0.9]) body.add(box(1.6, 0.45, 0.03, mat('#f4f4f4'), 0, 0.68, z));
        const red = new THREE.MeshStandardMaterial({
          color: '#ff2a2a',
          emissive: new THREE.Color('#ff0000'),
          emissiveIntensity: 0,
        });
        const blue = new THREE.MeshStandardMaterial({
          color: '#2a5bff',
          emissive: new THREE.Color('#0040ff'),
          emissiveIntensity: 0,
        });
        body.add(box(0.3, 0.12, 1.3, M.black, -0.1, 1.54, 0));
        body.add(box(0.28, 0.12, 0.6, red, -0.1, 1.56, -0.34, { cast: false }));
        body.add(box(0.28, 0.12, 0.6, blue, -0.1, 1.56, 0.34, { cast: false }));
        lightBar = [red, blue];
      }
      rear.position.set(-2.36, 0.45, 0);
      break;
    }
    case 'pickup': {
      length = 5.3;
      width = 1.95;
      wheelX = 1.7;
      body.add(box(1.55, 0.6, 1.9, paint, 1.85, 0.75, 0));
      body.add(box(1.5, 0.95, 1.9, paint, 0.38, 0.93, 0));
      // Glass pokes out past the cab's sides and nose but stays under the roof, so no face is flush.
      body.add(box(1.4, 0.46, 1.92, glass, 0.45, 1.18, 0, { cast: false }));
      body.add(box(1.25, 0.06, 1.84, paint, 0.38, 1.42, 0));
      body.add(box(2.05, 0.18, 1.9, paint, -1.4, 0.52, 0));
      body.add(box(2.05, 0.5, 0.08, paint, -1.4, 0.86, 0.91));
      body.add(box(2.05, 0.5, 0.08, paint, -1.4, 0.86, -0.91));
      body.add(box(0.08, 0.5, 1.9, paint, -2.4, 0.86, 0));
      body.add(box(0.1, 0.16, 1.95, M.chrome, 2.65, 0.5, 0));
      body.add(box(0.1, 0.16, 1.95, M.chrome, -2.47, 0.5, 0));
      rear.position.set(-2.52, 0.42, 0);
      break;
    }
    case 'van': {
      length = 5;
      wheelX = 1.6;
      body.add(box(4.6, 1.3, 1.9, paint, -0.1, 1.05, 0));
      const nose = box(0.9, 0.6, 1.88, paint, 2.3, 0.75, 0);
      body.add(nose);
      const ws = box(0.06, 0.6, 1.7, glass, 2.12, 1.4, 0, { cast: false });
      ws.rotation.z = -0.5;
      body.add(ws);
      for (const z of [-0.96, 0.96]) body.add(box(3.4, 0.45, 0.02, glass, -0.4, 1.35, z, { cast: false }));
      body.add(box(4.0, 0.12, 0.03, mat('#8a5a2b'), -0.2, 0.85, 0.96));
      rear.position.set(-2.42, 0.45, 0);
      break;
    }
  }

  const tire = M.rubber;
  const hub = spec.rusty ? mat('#6e5a48') : M.chrome;
  for (const x of [-wheelX, wheelX]) {
    for (const z of [-1, 1]) {
      const w = cyl(0.34, 0.34, 0.26, tire, x, 0.34, z * (width / 2 - 0.04), 14);
      w.rotation.x = Math.PI / 2;
      const cap = cyl(0.2, 0.2, 0.02, hub, 0, z * 0.13, 0, 10, { cast: false });
      w.add(cap);
      wheels.push(w);
      group.add(w);
    }
  }

  if (spec.onBlocks) {
    body.position.y = 0.32;
    for (const w of wheels) w.visible = false;
    for (const x of [-wheelX, wheelX])
      for (const z of [-0.65, 0.65]) {
        group.add(box(0.4, 0.4, 0.2, M.cinder, x, 0.2, z));
        group.add(box(0.2, 0.2, 0.4, M.cinder, x, 0.5, z));
      }
  }
  return { group, wheels, body, lightBar, rear, length, width };
}

/** Truck Nutz: a tasteful accessory for the discerning gentleman. */
export function buildTruckNutz(): THREE.Group {
  const g = new THREE.Group();
  const m = mat('#b81d24', { rough: 0.3, metal: 0.3 });
  g.add(box(0.04, 0.12, 0.04, M.chrome, 0, -0.04, 0));
  g.add(sphere(0.085, m, -0.04, -0.17, -0.05, 10));
  g.add(sphere(0.085, m, -0.04, -0.17, 0.05, 10));
  return g;
}

export function buildBoatOnTrailer(color = '#e8e4da'): THREE.Group {
  const g = new THREE.Group();
  const shape = new THREE.Shape();
  shape.moveTo(-2.2, 0.15);
  shape.lineTo(1.6, 0.15);
  shape.lineTo(2.5, 0.8);
  shape.lineTo(-2.2, 0.8);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: 1.6, bevelEnabled: false });
  geo.translate(0, 0, -0.8);
  g.add(mesh(geo, mat(color), 0, 0.45, 0));
  g.add(box(4.0, 0.12, 1.5, mat('#1f5fbf'), -0.1, 1.27, 0, { cast: false }));
  g.add(box(4.6, 0.1, 0.1, M.black, 0, 0.42, 0.55));
  g.add(box(4.6, 0.1, 0.1, M.black, 0, 0.42, -0.55));
  for (const z of [-0.75, 0.75]) {
    const w = cyl(0.25, 0.25, 0.18, M.rubber, -0.4, 0.25, z, 10);
    w.rotation.x = Math.PI / 2;
    g.add(w);
  }
  g.add(box(0.3, 0.6, 0.25, M.black, -2.35, 0.9, 0));
  return g;
}

/** An 18-wheeler: cab up front (+x), box trailer behind. Origin on the ground at its middle. */
export function buildSemi(cabColor: string, trailerSpec: SignSpec): BuiltCar {
  const group = new THREE.Group();
  const body = new THREE.Group();
  group.add(body);
  const paint = mat(cabColor, { rough: 0.4, metal: 0.35 });
  const wheels: THREE.Mesh[] = [];
  const width = 2.55;
  const length = 16.4;
  // Cab: hood, cab box, sleeper and the chrome that makes it a real truck.
  body.add(box(1.7, 1.25, 2.3, paint, 7.25, 1.5, 0));
  body.add(box(2.3, 2.5, width, paint, 5.2, 2.25, 0));
  body.add(box(0.06, 0.9, 2.2, M.darkGlass, 6.37, 2.75, 0, { cast: false }));
  for (const z of [-1, 1]) body.add(box(1.0, 0.7, 0.04, M.darkGlass, 5.6, 2.75, z * 1.29, { cast: false }));
  body.add(box(0.12, 0.9, 1.6, M.chrome, 8.12, 1.45, 0));
  body.add(box(0.2, 0.25, 2.5, M.chrome, 8.2, 0.75, 0));
  for (const z of [-1.05, 1.05]) {
    const stack = cyl(0.09, 0.09, 2.6, M.chrome, 3.95, 3.1, z, 8);
    body.add(stack);
  }
  for (const z of [-0.98, 0.98])
    body.add(box(0.05, 0.14, 0.24, mat('#fff3c0', { emissive: '#7a6a30' }), 8.12, 1.65, z, { cast: false }));
  body.add(box(1.4, 0.3, 2.2, M.black, 3.25, 1.15, 0));
  // Trailer: a long box with the hauler's name down both sides.
  const box53 = mat('#e9e7e1', { rough: 0.6, metal: 0.2 });
  // The trailer stops short of the cab so their sides are never flush.
  body.add(box(12.2, 3.0, width, box53, -2.1, 2.75, 0));
  body.add(box(12.2, 0.2, 2.4, M.black, -2.1, 1.15, 0));
  const side = texMat(signTexture(trailerSpec));
  for (const s of [-1, 1]) {
    const decal = mesh(new THREE.PlaneGeometry(11, 2.4), side, -2.1, 2.8, s * (width / 2 + 0.02), {
      cast: false,
    });
    decal.rotation.y = s > 0 ? 0 : Math.PI;
    body.add(decal);
  }
  for (const s of [-1, 1]) body.add(box(10.5, 0.35, 0.08, M.steel, -2.4, 0.95, s * 1.1, { cast: false }));
  const rear = new THREE.Group();
  rear.position.set(-8.25, 1.0, 0);
  body.add(rear);
  for (const z of [-0.9, 0.9])
    body.add(box(0.05, 0.12, 0.3, mat('#c4202a', { emissive: '#5a0000' }), -8.23, 1.35, z, { cast: false }));
  // Steer axle, two drive axles, two trailer axles.
  for (const x of [7.1, 4.3, 3.1, -6.0, -7.2]) {
    for (const z of [-1, 1]) {
      const w = cyl(0.52, 0.52, x === 7.1 ? 0.3 : 0.55, M.rubber, x, 0.52, z * (width / 2 - 0.2), 14);
      w.rotation.x = Math.PI / 2;
      const cap = cyl(0.3, 0.3, 0.02, M.chrome, 0, z * (x === 7.1 ? 0.16 : 0.28), 0, 10, { cast: false });
      w.add(cap);
      wheels.push(w);
      group.add(w);
    }
  }
  return { group, wheels, body, rear, length, width };
}
